import CopyableId from "@/components/common/CopyableId";
import React, { useEffect, useState, useCallback } from "react";
import {
  FileText, Download, Activity, Pill, Stethoscope, HeartPulse, Video,
  Phone, MapPin, ClipboardList, AlertTriangle, FileClock, Syringe, ShieldAlert,
  ClipboardCheck, Target,
} from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/common/StatusBadge";
import { api, API, formatApiError, getToken } from "@/lib/api";

const fmt = (s) => (s ? String(s).slice(0, 16).replace("T", " ") : "—");
const fmtD = (s) => (s ? String(s).slice(0, 10) : "—");
const cap = (s) => (s ? String(s).replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()) : "—");

const SEV_COLOR = {
  critical: "#EF4444", high: "#F97316", severe: "#EF4444",
  life_threatening: "#B91C1C", moderate: "#F59E0B", warning: "#F59E0B",
  mild: "#10B981", low: "#10B981", routine: "#6B7280",
};

function Kpi({ icon: Icon, label, value, sub }) {
  return (
    <div className="hah-card p-3 flex items-center gap-3" data-testid={`cr-kpi-${label}`}>
      <div className="w-9 h-9 rounded-lg bg-[#EAF8FA] text-[#0B7E8C] flex items-center justify-center shrink-0">
        <Icon className="w-[18px] h-[18px]" />
      </div>
      <div className="min-w-0">
        <div className="text-lg font-semibold text-[#1F2937] leading-none">{value}</div>
        <div className="text-[11px] text-[#6B7280] mt-1 truncate">{label}{sub ? ` · ${sub}` : ""}</div>
      </div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="text-sm text-[#94A3B8] italic py-4">{text}</div>;
}

function SectionCard({ title, icon: Icon, count, children, testId }) {
  return (
    <div className="hah-card p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-4 h-4 text-[#0B7E8C]" />}
        <h3 className="text-base font-semibold text-[#1F2937]">{title}</h3>
        {count != null && <span className="text-xs text-[#6B7280]">({count})</span>}
      </div>
      {children}
    </div>
  );
}

function Row({ children }) {
  return <div className="flex items-start gap-3 py-2 border-t border-[#F1F5F9] first:border-t-0">{children}</div>;
}

export default function CareRecord({ requestId, refNumber, defaultScope = "episode" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [scope, setScope] = useState(defaultScope);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/onboarding/requests/${requestId}/care-record?scope=${scope}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [requestId, scope]);

  const downloadPdf = useCallback(async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/onboarding/requests/${requestId}/care-record/pdf?scope=${scope}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("PDF export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `care-record-${scope === "episode" ? (refNumber || requestId) : (data?.patient?.mrn || requestId) + "-full"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Care Record PDF downloaded");
    } catch (e) {
      toast.error("Could not generate the PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  }, [requestId, data, refNumber, scope]);

  if (loading) return <div className="py-10 text-center text-sm text-[#6B7280]" data-testid="cr-loading">Loading care record…</div>;
  if (!data) return <Empty text="No care record available." />;

  const p = data.patient || {};
  const counts = data.counts || {};
  const rpm = data.rpm || {};

  return (
    <div className="space-y-4" data-testid="care-record">
      {/* Patient banner + export */}
      <div className="hah-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="hah-section-title mb-1">Patient Care Record (360°)</div>
            <h3 className="text-lg font-semibold text-[#1F2937] flex items-center gap-2 flex-wrap">
              {p.name || "—"}
              {p.mrn && <span className="font-mono text-sm text-[#0B7E8C]"><CopyableId value={p.mrn} kind="MRN" /></span>}
              {!data.linked && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#F59E0B14] text-[#92400E] border border-[#F59E0B33]">Not yet linked to a clinical profile</span>}
              {data.disposition && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#64748B1A] text-[#475569] border border-[#64748B33]" data-testid="cr-disposition">{data.disposition.label}</span>}
            </h3>
            <div className="text-xs text-[#6B7280] mt-1 flex flex-wrap gap-x-4 gap-y-1">
              <span>National ID: <CopyableId value={p.national_id} kind="Identity number" /></span>
              <span>Gender: {cap(p.gender)}</span>
              <span>DOB: {fmtD(p.dob)}</span>
              <span>Phone: {p.phone || "—"}</span>
              <span>Status: {cap(p.status)}</span>
              {data.facility_name && <span>Facility: {data.facility_name}</span>}
            </div>
            {p.primary_diagnosis && <div className="text-sm text-[#1F2937] mt-2"><span className="text-[#6B7280]">Primary diagnosis: </span>{p.primary_diagnosis}</div>}
            {data.scope === "episode" && data.window && (
              <div className="text-xs mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#EAF8FA] text-[#0B7E8C] border border-[#0B7E8C22]" data-testid="cr-window">
                <FileClock className="w-3.5 h-3.5" />
                Episode {data.episode?.ref_number} · {fmtD(data.window.start)} → {data.window.is_current ? "current" : fmtD(data.window.end)}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <div className="inline-flex rounded-lg border border-[#E5E7EB] bg-white p-0.5" data-testid="cr-scope-toggle">
              <button
                onClick={() => setScope("episode")}
                className={`text-xs px-3 py-1.5 rounded-md transition-colors ${scope === "episode" ? "bg-[#0B7E8C] text-white" : "text-[#6B7280] hover:text-[#0B7E8C]"}`}
                data-testid="cr-scope-episode"
              >This episode only</button>
              <button
                onClick={() => setScope("full")}
                className={`text-xs px-3 py-1.5 rounded-md transition-colors ${scope === "full" ? "bg-[#0B7E8C] text-white" : "text-[#6B7280] hover:text-[#0B7E8C]"}`}
                data-testid="cr-scope-full"
              >Full history</button>
            </div>
            <Button onClick={downloadPdf} disabled={downloading} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="cr-download-pdf">
              <Download className="w-4 h-4 mr-1.5" /> {downloading ? "Preparing…" : "Download PDF"}
            </Button>
          </div>
        </div>
      </div>

      {/* Allergy banner */}
      {(data.allergies || []).length > 0 && (
        <div className="p-4 rounded-lg bg-[#FEF2F2] border border-[#FECACA]" data-testid="cr-allergies">
          <div className="flex items-center gap-2 text-[#B91C1C] font-semibold mb-1">
            <ShieldAlert className="w-4 h-4" /> Allergies ({data.allergies.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {data.allergies.map((a) => (
              <span key={a.id} className="text-xs px-2 py-1 rounded-full border" style={{ color: SEV_COLOR[a.severity] || "#B91C1C", borderColor: (SEV_COLOR[a.severity] || "#B91C1C") + "55" }}>
                <b>{a.allergen}</b> · {cap(a.severity)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
        <Kpi icon={FileClock} label="Episodes" value={counts.episodes || 0} />
        <Kpi icon={Pill} label="Orders" value={counts.orders || 0} sub={`${counts.active_orders || 0} active`} />
        <Kpi icon={FileText} label="Notes" value={counts.notes || 0} />
        <Kpi icon={AlertTriangle} label="Alerts" value={counts.alerts || 0} sub={`${counts.open_alerts || 0} open`} />
        <Kpi icon={Video} label="Teleconsults" value={counts.teleconsults || 0} />
        <Kpi icon={Stethoscope} label="Encounters" value={counts.encounters || 0} />
        <Kpi icon={Phone} label="Hotline" value={counts.hotline_sessions || 0} />
        <Kpi icon={MapPin} label="Home visits" value={counts.home_visits || 0} />
      </div>

      <Tabs defaultValue="timeline">
        <TabsList className="bg-[#F5F7FA] flex-wrap h-auto">
          <TabsTrigger value="timeline" data-testid="cr-tab-timeline">Care Timeline</TabsTrigger>
          <TabsTrigger value="care-plan" data-testid="cr-tab-care-plan">Care Plan</TabsTrigger>
          <TabsTrigger value="episodes" data-testid="cr-tab-episodes">Episodes</TabsTrigger>
          <TabsTrigger value="notes" data-testid="cr-tab-notes">Clinical Notes</TabsTrigger>
          <TabsTrigger value="orders" data-testid="cr-tab-orders">Orders & MAR</TabsTrigger>
          <TabsTrigger value="monitoring" data-testid="cr-tab-monitoring">Monitoring</TabsTrigger>
          <TabsTrigger value="consults" data-testid="cr-tab-consults">Consults & Visits</TabsTrigger>
        </TabsList>

        {/* TIMELINE */}
        <TabsContent value="timeline" className="mt-4">
          <SectionCard title="Care Timeline" icon={Activity} count={(data.timeline || []).length} testId="cr-timeline">
            {(data.timeline || []).length === 0 ? <Empty text="No clinical activity recorded yet." /> : (
              <ol className="relative border-l border-[#E5E7EB] ml-2 space-y-4">
                {data.timeline.map((e, i) => (
                  <li key={i} className="ml-4 relative" data-testid={`cr-timeline-item-${i}`}>
                    <span className="absolute -left-[22px] top-1.5 w-2.5 h-2.5 rounded-full bg-[#0B7E8C] ring-4 ring-white" />
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#EAF8FA] text-[#0B7E8C]">{cap(e.category)}</span>
                      <span className="font-medium text-[#1F2937] text-sm">{e.title}</span>
                      {e.status && <StatusBadge status={e.status} />}
                      <span className="text-xs text-[#94A3B8]">{fmt(e.at)}</span>
                    </div>
                    {e.description && <div className="text-sm text-[#6B7280] mt-0.5">{e.description}</div>}
                  </li>
                ))}
              </ol>
            )}
          </SectionCard>
        </TabsContent>

        {/* CARE PLAN */}
        <TabsContent value="care-plan" className="mt-4">
          {!data.care_plan ? <SectionCard title="Care Plan" icon={ClipboardCheck} testId="cr-care-plan"><Empty text="No care plan on file." /></SectionCard> : (() => {
            const cp = data.care_plan; const c = cp.content || {}; const vp = c.visit_plan || {};
            const team = cp.care_team || {}; const dis = cp.discharge || {};
            return (
              <SectionCard title="Care Plan" icon={ClipboardCheck} testId="cr-care-plan"
                count={undefined}>
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <StatusBadge status={cp.status} />
                  {cp.version > 0 && <span className="text-xs text-[#6B7280]">v{cp.version}</span>}
                  {cp.published_by_name && <span className="text-xs text-[#94A3B8]">Published by {cp.published_by_name}</span>}
                  <span className="text-xs text-[#0B7E8C] ml-auto">Discharge criteria {dis.met || 0}/{dis.total || 0} met</span>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div data-testid="cr-cp-goals">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280] mb-1.5 flex items-center gap-1.5"><Target className="w-3.5 h-3.5 text-[#0B7E8C]" />Goals</div>
                    {(c.goals || []).length === 0 ? <Empty text="—" /> : (c.goals || []).map((g, i) => (
                      <div key={i} className="flex items-center justify-between py-1 border-t border-[#F1F5F9] first:border-t-0">
                        <span className="text-sm text-[#1F2937]">{g.goal}{g.target ? ` — ${g.target}` : ""}</span>
                        {g.status && <StatusBadge status={g.status} />}
                      </div>
                    ))}
                  </div>
                  <div data-testid="cr-cp-discharge">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280] mb-1.5 flex items-center gap-1.5"><ClipboardCheck className="w-3.5 h-3.5 text-[#0B7E8C]" />Discharge Criteria</div>
                    {(c.discharge_criteria || []).map((cr, i) => (
                      <div key={i} className="flex items-center gap-2 py-0.5">
                        <span className={`w-2 h-2 rounded-full ${cr.met ? "bg-[#059669]" : "bg-[#CBD5E1]"}`} />
                        <span className={`text-sm ${cr.met ? "text-[#059669] line-through" : "text-[#1F2937]"}`}>{cr.criterion}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280] mb-1.5">Interventions & Monitoring</div>
                    {(c.interventions || []).map((it, i) => <div key={i} className="text-sm text-[#1F2937]">{it.type} <span className="text-[#94A3B8]">· {it.frequency}</span></div>)}
                    {((c.monitoring || {}).vitals || []).map((m, i) => <div key={i} className="text-sm text-[#1F2937]">{m.vital} <span className="text-[#94A3B8]">· {m.frequency}</span></div>)}
                    {(c.monitoring || {}).rpm_enroll && <div className="text-xs text-[#0B7E8C] mt-1 inline-flex items-center gap-1"><HeartPulse className="w-3.5 h-3.5" />RPM enrolled</div>}
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280] mb-1.5">Visit Plan & Team</div>
                    <div className="text-sm text-[#1F2937]">Physician {vp.physician_visits_per_week ?? "—"}/wk · Nurse {vp.nurse_visits_per_day ?? "—"}/day · Tele {vp.teleconsults_per_week ?? "—"}/wk</div>
                    <div className="text-sm text-[#6B7280]">Exp. discharge {vp.expected_discharge_date || "—"}</div>
                    <div className="text-sm mt-1"><span className="text-[#6B7280]">Physician: </span>{team.physician_name || "—"}</div>
                    <div className="text-sm"><span className="text-[#6B7280]">Nurses: </span>{(team.nurse_names || []).join(", ") || "—"}</div>
                  </div>
                </div>
              </SectionCard>
            );
          })()}
        </TabsContent>


        {/* EPISODES */}
        <TabsContent value="episodes" className="mt-4">
          <SectionCard title="Treatment Episodes" icon={FileClock} count={(data.episodes || []).length} testId="cr-episodes">
            {(data.episodes || []).length === 0 ? <Empty text="No episodes found." /> : data.episodes.map((ep) => (
              <Row key={ep.id}>
                <div className="w-9 h-9 rounded-lg bg-[#EAF8FA] text-[#0B7E8C] flex items-center justify-center shrink-0"><FileClock className="w-4 h-4" /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm font-semibold text-[#0B7E8C]">{ep.ref_number}</span>
                    <StatusBadge status={ep.status} />
                  </div>
                  <div className="text-xs text-[#6B7280] mt-0.5">{ep.primary_diagnosis || "—"}{ep.requested_service ? ` · ${ep.requested_service}` : ""}</div>
                  <div className="text-[11px] text-[#94A3B8] mt-1 flex flex-wrap gap-3">
                    <span>Initiated {fmtD(ep.created_at)}</span>
                    {ep.operational_approved_at && <span>Approved {fmtD(ep.operational_approved_at)}</span>}
                    {ep.visit_completed_at && <span>Visit done {fmtD(ep.visit_completed_at)}</span>}
                  </div>
                </div>
              </Row>
            ))}
          </SectionCard>
        </TabsContent>

        {/* NOTES */}
        <TabsContent value="notes" className="mt-4">
          <SectionCard title="Clinical Notes" icon={FileText} count={(data.notes || []).length} testId="cr-notes">
            {(data.notes || []).length === 0 ? <Empty text="No clinical notes recorded." /> : data.notes.map((n) => (
              <Row key={n.id}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#EAF8FA] text-[#0B7E8C]">{cap(n.category)}</span>
                    <span className="text-sm font-medium text-[#1F2937]">{n.author_name || "—"}</span>
                    {n.signed_at && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#ECFDF5] text-[#059669]">Signed</span>}
                    <span className="text-xs text-[#94A3B8]">{fmt(n.signed_at || n.created_at)}</span>
                  </div>
                  <div className="text-sm text-[#1F2937] mt-1 space-y-0.5">
                    {n.subjective && <div><span className="text-[#6B7280]">S: </span>{n.subjective}</div>}
                    {n.objective && <div><span className="text-[#6B7280]">O: </span>{n.objective}</div>}
                    {n.assessment && <div><span className="text-[#6B7280]">A: </span>{n.assessment}</div>}
                    {n.plan && <div><span className="text-[#6B7280]">P: </span>{n.plan}</div>}
                    {n.narrative && <div>{n.narrative}</div>}
                  </div>
                </div>
              </Row>
            ))}
          </SectionCard>
        </TabsContent>

        {/* ORDERS & MAR */}
        <TabsContent value="orders" className="mt-4 space-y-4">
          <SectionCard title="Orders" icon={Pill} count={(data.orders || []).length} testId="cr-orders">
            {(data.orders || []).length === 0 ? <Empty text="No orders recorded." /> : data.orders.map((o) => (
              <Row key={o.id}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#475569]">{cap(o.order_type)}</span>
                    <span className="text-sm font-medium text-[#1F2937]">{o.medication_name || o.test_name || o.description || cap(o.order_type)}</span>
                    <StatusBadge status={o.status} />
                  </div>
                  <div className="text-xs text-[#6B7280] mt-0.5">
                    {[o.dose, o.frequency, o.route].filter(Boolean).join(" · ") || o.clinical_indication || "—"}
                  </div>
                  <div className="text-[11px] text-[#94A3B8] mt-1">
                    Ordered by {o.ordered_by_name || "—"} {o.signed_at ? `· signed ${fmt(o.signed_at)}` : ""}
                  </div>
                </div>
              </Row>
            ))}
          </SectionCard>
          <SectionCard title="Medication Administration (recent)" icon={Syringe} count={(data.mar || []).length} testId="cr-mar">
            {(data.mar || []).length === 0 ? <Empty text="No medication administration records." /> : (
              <div className="overflow-x-auto">
                <div className="overflow-x-auto"><table className="w-full text-sm">
                  <thead className="bg-[#F5F7FA] text-[11px] uppercase tracking-wider text-[#6B7280]">
                    <tr>
                      <th className="text-left px-3 py-2">Scheduled</th><th className="text-left px-3 py-2">Medication</th>
                      <th className="text-left px-3 py-2">Dose</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Given by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.mar.slice(0, 40).map((mr) => (
                      <tr key={mr.id} className="border-t border-[#E5E7EB]">
                        <td className="px-3 py-2 text-xs text-[#6B7280]">{fmt(mr.scheduled_at)}</td>
                        <td className="px-3 py-2 text-[#1F2937]">{mr.medication_name || "—"}</td>
                        <td className="px-3 py-2 text-[#6B7280]">{mr.dose || "—"}</td>
                        <td className="px-3 py-2"><StatusBadge status={mr.status} /></td>
                        <td className="px-3 py-2 text-[#6B7280]">{mr.administered_by_name || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              </div>
            )}
          </SectionCard>
        </TabsContent>

        {/* MONITORING */}
        <TabsContent value="monitoring" className="mt-4 space-y-4">
          <SectionCard title="Latest Vitals" icon={HeartPulse} testId="cr-vitals">
            {(rpm.vitals || []).length === 0 ? <Empty text="No vitals recorded." /> : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                {rpm.vitals.map((v, i) => (
                  <div key={i} className="rounded-lg border border-[#E5E7EB] p-3">
                    <div className="text-[11px] uppercase tracking-wider text-[#6B7280]">{cap(v.vital_type)}</div>
                    <div className="text-lg font-semibold text-[#1F2937]">{v.value} <span className="text-xs text-[#94A3B8]">{v.unit}</span></div>
                    <div className="text-[10px] text-[#94A3B8] mt-0.5">{fmt(v.taken_at)}</div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
          <SectionCard title="RPM Alerts" icon={AlertTriangle} count={(rpm.alerts || []).length} testId="cr-alerts">
            {(rpm.alerts || []).length === 0 ? <Empty text="No alerts." /> : rpm.alerts.map((a) => (
              <Row key={a.id}>
                <div className="w-2 h-2 rounded-full mt-2 shrink-0" style={{ backgroundColor: SEV_COLOR[a.severity] || "#6B7280" }} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-[#1F2937]">{a.title || cap(a.alert_type) || "RPM alert"}</span>
                    <span className="text-[10px] uppercase px-1.5 py-0.5 rounded" style={{ color: SEV_COLOR[a.severity], backgroundColor: (SEV_COLOR[a.severity] || "#6B7280") + "1A" }}>{cap(a.severity)}</span>
                    <StatusBadge status={a.status} />
                    <span className="text-xs text-[#94A3B8]">{fmt(a.created_at)}</span>
                  </div>
                  {a.description && <div className="text-xs text-[#6B7280] mt-0.5">{a.description}</div>}
                </div>
              </Row>
            ))}
          </SectionCard>
        </TabsContent>

        {/* CONSULTS & VISITS */}
        <TabsContent value="consults" className="mt-4 space-y-4">
          <SectionCard title="Teleconsultations" icon={Video} count={(data.teleconsults || []).length} testId="cr-teleconsults">
            {(data.teleconsults || []).length === 0 ? <Empty text="No teleconsultations." /> : data.teleconsults.map((t) => (
              <Row key={t.id}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-[#1F2937]">{cap(t.consultation_type) || "Teleconsult"}</span>
                    <StatusBadge status={t.status} />
                    <span className="text-xs text-[#94A3B8]">{fmt(t.scheduled_at)}</span>
                  </div>
                  <div className="text-[11px] text-[#94A3B8] mt-0.5">Provider: {t.provider_name || "—"}{t.notes ? ` · ${t.notes}` : ""}</div>
                </div>
              </Row>
            ))}
          </SectionCard>
          <SectionCard title="Clinical Encounters" icon={Stethoscope} count={(data.encounters || []).length} testId="cr-encounters">
            {(data.encounters || []).length === 0 ? <Empty text="No encounters." /> : data.encounters.map((e, i) => (
              <Row key={i}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-[#1F2937]">{cap(e.outcome) || "Encounter"}</span>
                    <span className="text-xs text-[#94A3B8]">{fmt(e.ended_at)}</span>
                  </div>
                  {e.assessment && <div className="text-xs text-[#6B7280] mt-0.5">{e.assessment}</div>}
                  {e.diagnosis && <div className="text-[11px] text-[#94A3B8] mt-0.5">Dx: {e.diagnosis}</div>}
                </div>
              </Row>
            ))}
          </SectionCard>
          <SectionCard title="Nurse Hotline Sessions" icon={Phone} count={(data.hotline_sessions || []).length} testId="cr-hotline">
            {(data.hotline_sessions || []).length === 0 ? <Empty text="No hotline sessions." /> : data.hotline_sessions.map((s) => (
              <Row key={s.id}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-[#0B7E8C]">{s.record_id || "—"}</span>
                    <StatusBadge status={s.status} />
                    <span className="text-xs text-[#94A3B8]">{fmt(s.created_at)}</span>
                  </div>
                  {s.chief_complaint?.description && <div className="text-xs text-[#6B7280] mt-0.5">{s.chief_complaint.description}</div>}
                </div>
              </Row>
            ))}
          </SectionCard>
          <SectionCard title="Home Visits" icon={MapPin} count={(data.home_visits || []).length} testId="cr-visits">
            {(data.home_visits || []).length === 0 ? <Empty text="No home visits." /> : data.home_visits.map((v) => (
              <Row key={v.id}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-[#1F2937]">{cap(v.visit_type) || "Home visit"}</span>
                    <StatusBadge status={v.status} />
                    <span className="text-xs text-[#94A3B8]">{fmt(v.scheduled_at)}</span>
                  </div>
                  <div className="text-[11px] text-[#94A3B8] mt-0.5">Clinician: {v.clinician_name || "—"}{v.notes ? ` · ${v.notes}` : ""}</div>
                </div>
              </Row>
            ))}
          </SectionCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
